import { Button } from '@app/components/various/Button';
import { Checkout } from '@app/components/various/Icons';
import { getActivePaymentMethod, getIsBuyerCheckout, getIsLoggedIn, getIsPrepaymentEnabled, getUser } from '@app/ducks';
import { getIsSeller } from '@app/logic/User';
import { Order } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  isDisabled: boolean;
  isLoading: boolean;
  orderDetails: Order.Single;
  className?: string;
}

export const CheckoutButton = ({ orderDetails, isDisabled, isLoading, className }: Props) => {
  const { t } = useTranslation(['checkout', 'payments']);

  const isPrepaymentEnabled = useSelector(getIsPrepaymentEnabled);
  const user = useSelector(getUser);
  const activePaymentMethod = useSelector(getActivePaymentMethod);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isBuyerCheckout = useSelector(getIsBuyerCheckout);
  const isSeller = getIsSeller(user);

  const isPayLaterSelected = activePaymentMethod === 'pay_later';
  const canCheckout = isSeller || (isLoggedIn && isBuyerCheckout);
  const canPay = canCheckout || !isPayLaterSelected;
  const content = React.useMemo(() => {
    if (isPrepaymentEnabled && canPay) {
      if (isPayLaterSelected) {
        return t('payments:confirm_and_pay_later');
      }

      return t('payments:confirm_and_pay');
    }

    if (canCheckout) {
      return t('checkout:confirm_order');
    }

    if (orderDetails.order.status === 'checkoutRequested') {
      return t('checkout:update_checkout_request');
    }

    return t('checkout:request_checkout');
  }, [canCheckout, canPay, isPayLaterSelected, isPrepaymentEnabled, orderDetails.order.status, t]);

  return (
    <Button
      icon={Checkout}
      isLoading={isLoading}
      disabled={isDisabled}
      data-testid="checkoutButton"
      type="submit"
      form="checkoutForm"
      isFluid
      className={className}
    >
      {content}
    </Button>
  );
};
